import React from "react";
import { BoxChooser } from "./box_chooser.jsx";
import { boxLabel, request, useBoxAllocation } from "./box_allocation.js";
import { LotIdentity } from "./lot_identity.jsx";
import { isWithdrawn, resultLine } from "./stable_result.jsx";
import { sydneyWhen } from "./time.js";

const KINDS = [["xray", "X-ray"], ["scope", "Scope"], ["report", "Report"]];
const TICK = { yes: "✓", no: "–", unknown: "?" };
const TICK_WORDS = { yes: "lodged", no: "nothing lodged", unknown: "not known" };

// What the whole page may say about lodgements (spec §4.3). "Nothing lodged" is
// never derived from these: only a known list for a horse can say it.
export function lodgedNote(lodged) {
  switch (lodged?.status) {
    case "not_published": return "The Repository doesn't yet tell us what's been lodged for each horse. This fills in when it does.";
    case "unavailable": return "What's been lodged can't be loaded right now.";
    case "stale": return `As of ${sydneyWhen(lodged.asOf)}. The Repository hasn't answered since.`;
    default: return "";
  }
}

function boxText(allocation, lot) {
  if (!allocation.data) return allocation.loading ? "Box …" : "Box unavailable";
  return lot?.box ? `Box ${boxLabel(lot.box)}` : "No box";
}

// A card pages through its horse's catalogue photos (owner, 25 Sep 2026). The
// list carries only the first photo and the count, so the card asks for the
// horse's full gallery (vendor.stable.horse@1) the first time it pages, once.
function CardPhotos({ horse, loadPhotos, open }) {
  const [photos, setPhotos] = React.useState(horse.photo ? [horse.photo] : []);
  const [shown, setShown] = React.useState(0);
  const [waiting, setWaiting] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const touch = React.useRef(null);
  const count = Math.max(horse.photos, photos.length);

  async function step(by) {
    const next = shown + by;
    if (next < 0 || next >= count || waiting) return;
    if (next >= photos.length) {
      setWaiting(true);
      try {
        const gallery = await loadPhotos(horse.id);
        setPhotos(gallery);
        setFailed(false);
        if (next < gallery.length) setShown(next);
      } catch (_) {
        setFailed(true);
      } finally { setWaiting(false); }
      return;
    }
    setShown(next);
  }

  return <div className="horse-card-photo"
    onTouchStart={event => { touch.current = event.touches[0].clientX; }}
    onTouchEnd={event => {
      const start = touch.current;
      touch.current = null;
      if (start == null) return;
      const moved = event.changedTouches[0].clientX - start;
      if (moved < -40) step(1);
      if (moved > 40) step(-1);
    }}>
    <button type="button" className="horse-card-photo-open" aria-label={`Open Lot ${horse.number}`} onClick={open}>
      {photos[shown]
        ? <img src={photos[shown]} alt="" loading="lazy" referrerPolicy="no-referrer"/>
        : <span className="horse-card-nophoto">No catalogue photo</span>}
    </button>
    <span className="horse-card-badge">Lot {horse.number}</span>
    {count > 1 && <>
      <button type="button" className="horse-card-nav prev" aria-label={`Previous photo of Lot ${horse.number}`}
        disabled={shown === 0 || waiting} onClick={() => step(-1)}>‹</button>
      <button type="button" className="horse-card-nav next" aria-label={`Next photo of Lot ${horse.number}`}
        disabled={shown >= count - 1 || waiting} onClick={() => step(1)}>›</button>
      <span className="horse-card-count">{failed ? "Photos unavailable" : `${shown + 1} / ${count}`}</span>
    </>}
  </div>;
}

function HorseCard({ horse, badge, canChoose, onChoose, go, loadPhotos }) {
  const open = () => go(`horse:${horse.id}`);
  const result = !isWithdrawn(horse, horse.result) && resultLine(horse.result);
  return <article className="horse-card" data-lot={horse.id}>
    <CardPhotos horse={horse} loadPhotos={loadPhotos} open={open}/>
    <button type="button" className="horse-card-open" onClick={open}>
      <LotIdentity lot={horse}/>
      {isWithdrawn(horse, horse.result) && <span className="tag withdrawn">Withdrawn</span>}
      {result && <span className={`horse-card-result ${horse.result.status === "SOLD" ? "sold" : "passed-in"}`} data-result="">{result}</span>}
    </button>
    <div className="horse-card-foot">
      {!horse.withdrawn && (canChoose
        ? <button type="button" className="box-badge" data-box-open={horse.id} aria-label={`${badge}. Change the box for Lot ${horse.number}`} onClick={onChoose}>{badge} ▾</button>
        : <span className="box-badge static">{badge}</span>)}
      <span className="horse-ticks">
        <span aria-label={`${horse.photos} photos`}>📷 {horse.photos}</span>
        <span aria-label={`${horse.videos} videos`}>🎥 {horse.videos}</span>
        {KINDS.map(([kind, label]) => <span key={kind} data-tick={kind} className={`tick ${horse.ticks[kind]}`}
          aria-label={`${label}: ${TICK_WORDS[horse.ticks[kind]]}`}>{label} {TICK[horse.ticks[kind]]}</span>)}
      </span>
    </div>
  </article>;
}

export function StableHorses({ sale, vendor, go, onDenied }) {
  const allocation = useBoxAllocation({ sale, vendor });
  const [data, setData] = React.useState(null);
  const [failed, setFailed] = React.useState(false);
  const [choosing, setChoosing] = React.useState("");
  const galleries = React.useRef(new Map());
  const path = `/collections/vendor.stable.horses@1/?${new URLSearchParams({ sale, vendor })}`;

  // One request per horse however often its card pages; a failure is not cached.
  const loadPhotos = React.useCallback(lot => {
    if (!galleries.current.has(lot)) {
      const pending = request(`/collections/vendor.stable.horse@1/?${new URLSearchParams({ sale, vendor, lot })}`)
        .then(value => value.photos);
      pending.catch(() => galleries.current.delete(lot));
      galleries.current.set(lot, pending);
    }
    return galleries.current.get(lot);
  }, [sale, vendor]);

  React.useEffect(() => {
    let active = true;
    request(path).then(value => { if (active) setData(value); })
      .catch(problem => {
        if (!active) return;
        if (problem.status === 404) onDenied(); else setFailed(true);
      });
    return () => { active = false; };
  }, [path]);

  if (failed) return <p role="alert" className="error-text">Your horses could not be loaded. Refresh to try again.</p>;
  if (!data) return <p role="status">Loading your horses…</p>;
  const lots = new Map(allocation.lots.map(lot => [lot.id, lot]));
  const writable = !allocation.locked && allocation.data?.canAssign === true;
  const note = lodgedNote(data.lodged);
  const unreadable = ["live", "stale"].includes(data.lodged.status)
    && data.horses.some(horse => Object.values(horse.ticks).includes("unknown"));
  const choosingLot = choosing ? lots.get(choosing) : null;
  return <div className="stable-horses">
    <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
      {allocation.pending && <button type="button" className="btn sm primary" disabled={allocation.busy}
        onClick={() => allocation.send(allocation.pending)}>Retry same save</button>}
      {allocation.error && !allocation.pending && <button type="button" className="btn sm" disabled={allocation.busy}
        onClick={allocation.refresh}>Refresh boxes</button>}
    </div>
    <p role="status" aria-live="polite" className="muted">{allocation.notice}</p>
    {allocation.error && <p role="alert" className="error-text">{allocation.error}</p>}
    {!data.horses.length && <p>You have no horses in this sale.</p>}
    <div className="horse-cards">
      {data.horses.map(horse => <HorseCard key={horse.id} horse={horse} go={go} loadPhotos={loadPhotos}
        badge={boxText(allocation, lots.get(horse.id))}
        canChoose={writable && lots.has(horse.id)}
        onChoose={() => setChoosing(horse.id)}/>)}
    </div>
    {note && <p className="caption" data-lodged-note="">{note}</p>}
    {unreadable && <p className="caption">? means the Repository's answer for that horse couldn't be read.</p>}
    {choosingLot && <BoxChooser allocation={allocation} lot={choosingLot} onClose={() => setChoosing("")}/>}
  </div>;
}
